// AI 서비스 - OpenAI GPT-3.5 기반
class AIService {
  constructor() {
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.baseURL = 'https://api.openai.com/v1/chat/completions';
  }

  isConfigured() {
    return !!this.apiKey;
  }

  handleError(error) {
    console.error('AI Service Error:', error);
    return {
      success: false,
      error: error.message || 'AI 서비스 오류가 발생했습니다.'
    };
  }

  // ---------- 내부 유틸 ----------
  // 첫 번째 JSON 오브젝트만 안전 추출
  extractFirstJSONObject(text) {
    if (typeof text !== 'string') return null;
    const m = text.match(/\{[\s\S]*\}$/);
    if (!m) return null;
    try { return JSON.parse(m[0]); } catch { return null; }
  }

  // 문자열 배열로 강제 변환
  toStringArray(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(v => String(v)).filter(Boolean);
    return [String(val)];
  }

  // 중복 제거 + 공백 트림
  dedupeTrim(arr) {
    const s = new Set();
    for (const v of arr) {
      const t = String(v).trim();
      if (t) s.add(t);
    }
    return Array.from(s);
  }

  // 다양한 변형 스키마를 우리가 원하는 스키마로 강제 매핑
  coerceToExpectedSchema(parsed) {
    // 1) 정식 키가 이미 있으면 그대로 사용
    let preferredGenres = this.toStringArray(parsed?.preferredGenres);
    let preferredMood = this.toStringArray(parsed?.preferredMood);
    let recommendedKeywords = this.toStringArray(parsed?.recommendedKeywords);
    let reasoning = typeof parsed?.reasoning === 'string' ? parsed.reasoning : '';

    // 2) 흔한 변형 키 보정
    if (preferredGenres.length === 0) {
      preferredGenres = this.toStringArray(parsed?.genres || parsed?.genre || parsed?.topGenres);
    }
    if (preferredMood.length === 0) {
      preferredMood = this.toStringArray(parsed?.moods || parsed?.mood || parsed?.moodTags);
    }
    if (recommendedKeywords.length === 0) {
      recommendedKeywords = this.toStringArray(parsed?.keywords || parsed?.searchTerms || parsed?.searchKeywords);
    }

    // 3) 모델이 recommendedSongs로 줄 때 보정
    if (Array.isArray(parsed?.recommendedSongs)) {
      const songs = parsed.recommendedSongs;
      const g = [];
      const m = [];
      const k = [];
      for (const s of songs) {
        if (s?.genre) g.push(s.genre);
        if (s?.mood) m.push(s.mood);
        // 제목/아티스트를 키워드로 쓰는 건 과도할 수 있어 보수적으로 처리:
        if (s?.keywords) k.push(...this.toStringArray(s.keywords));
      }
      if (preferredGenres.length === 0) preferredGenres = g;
      if (preferredMood.length === 0) preferredMood = m;
      if (recommendedKeywords.length === 0) recommendedKeywords = k;
      if (!reasoning && typeof parsed?.reasoning === 'string') reasoning = parsed.reasoning;
    }

    // 4) 정규화(트림/중복 제거)
    preferredGenres = this.dedupeTrim(preferredGenres);
    preferredMood = this.dedupeTrim(preferredMood);
    recommendedKeywords = this.dedupeTrim(recommendedKeywords);

    // 5) 결과 반환(빈 배열 허용: UI에서 처리)
    return { preferredGenres, preferredMood, recommendedKeywords, reasoning };
  }

  // ---------- 메인 호출 ----------
  async analyzeMusicTaste(playlistHistory, userPreferences = {}, language = 'ko') {
    if (!this.isConfigured()) {
      return { success: false, error: 'OpenAI API 키가 설정되지 않았습니다.' };
    }

    try {
      const prompt = this.buildMusicAnalysisPrompt(playlistHistory, userPreferences, language);

      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content:
                '너는 음악 취향 분석가다. 반드시 JSON만 출력하고, 아래 요구된 키 이외의 키(recommendedSongs 등)를 포함하지 마라.'
            },
            { role: 'user', content: prompt }
          ],
          max_tokens: 500,
          temperature: 0.3 // 구조 일관성 강화
        })
      });

      if (!response.ok) throw new Error(`API 요청 실패: ${response.status}`);

      const data = await response.json();
      const content = data?.choices?.[0]?.message?.content ?? '';

      // 1) JSON 추출
      const parsed = this.extractFirstJSONObject(content);

      // 2) 파싱 실패 시 빈 스키마 반환(테스트 문구/예시값 미삽입)
      if (!parsed) {
        return {
          success: true,
          analysis: content || '',
          recommendations: { preferredGenres: [], preferredMood: [], recommendedKeywords: [], reasoning: '' }
        };
      }

      // 3) 변형 스키마 보정 → 기대 스키마로 강제 변환
      const coerced = this.coerceToExpectedSchema(parsed);

      return {
        success: true,
        analysis: content,
        recommendations: coerced
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // 플레이리스트 내용을 포함한 고급 분석
  async analyzeMusicTasteWithContent(playlistHistory, userPreferences = {}, language = 'ko') {
    if (!this.isConfigured()) {
      return { success: false, error: 'OpenAI API 키가 설정되지 않았습니다.' };
    }

    try {
      // YouTube 서비스에서 플레이리스트 내용 가져오기
      const { default: youtubeService } = await import('./youtubeService.js');
      
      // 각 플레이리스트의 곡 정보 가져오기 (최대 3개)
      const enrichedHistory = [];
      for (let i = 0; i < Math.min(playlistHistory.length, 3); i++) {
        const playlist = playlistHistory[i];
        const playlistId = this.extractPlaylistId(playlist.url);
        
        if (playlistId) {
          const songs = await youtubeService.getPlaylistItems(playlistId, 5);
          enrichedHistory.push({
            ...playlist,
            songs: songs || []
          });
        } else {
          enrichedHistory.push(playlist);
        }
      }

      const prompt = this.buildMusicAnalysisPromptWithContent(enrichedHistory, userPreferences, language);

      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content:
                '너는 음악 취향 분석가다. 플레이리스트 제목과 실제 곡 정보를 모두 고려하여 정확한 장르를 분석하라. 반드시 JSON만 출력하고, 아래 요구된 키 이외의 키(recommendedSongs 등)를 포함하지 마라.'
            },
            { role: 'user', content: prompt }
          ],
          max_tokens: 800,
          temperature: 0.3
        })
      });

      if (!response.ok) throw new Error(`API 요청 실패: ${response.status}`);

      const data = await response.json();
      const content = data?.choices?.[0]?.message?.content ?? '';

      const parsed = this.extractFirstJSONObject(content);
      if (!parsed) {
        return {
          success: true,
          analysis: content || '',
          recommendations: { preferredGenres: [], preferredMood: [], recommendedKeywords: [], reasoning: '' }
        };
      }

      const coerced = this.coerceToExpectedSchema(parsed);

      return {
        success: true,
        analysis: content,
        recommendations: coerced
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // 플레이리스트 ID 추출
  extractPlaylistId(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('list');
    } catch (e) {
      return null;
    }
  }

  // ---------- 프롬프트 ----------
  buildMusicAnalysisPrompt(playlistHistory, userPreferences, language = 'ko') {
    const isKorean = language === 'ko';

    // 공통 형식 지시
    const STRICT_FORMAT = isKorean
      ? `출력 형식: JSON 하나의 오브젝트로만 응답.
반드시 다음 "키만" 포함하고 그 외 키는 절대 포함하지 마세요:
- preferredGenres: string[]   // 사용자가 선호하는 장르 (구체적이고 정확한 장르명)
- preferredMood: string[]     // 사용자의 선호 분위기/무드 (감정적 특성)
- recommendedKeywords: string[] // YouTube 검색용 키워드 (실제 검색 가능한 구체적 키워드)
- reasoning: string           // 분석 근거 (플레이리스트 제목과 재생 횟수를 기반으로 한 구체적 분석)`
      : `Output format: Respond with a single JSON object ONLY.
Include EXACTLY these keys and NO others:
- preferredGenres: string[] (specific and accurate genre names)
- preferredMood: string[] (emotional characteristics)
- recommendedKeywords: string[] (concrete searchable keywords for YouTube)
- reasoning: string (analysis based on playlist titles and play counts)`;

    if (!playlistHistory || playlistHistory.length === 0) {
      return `
${isKorean
  ? '사용자에게 플레이리스트 히스토리가 없습니다. 사용자 선호사항을 기반으로 일반적인 음악 취향을 분석하세요.'
  : 'The user has no playlist history. Analyze general music preferences based on user preferences.'}

${isKorean ? '사용자 선호사항:' : 'User Preferences:'} ${JSON.stringify(userPreferences)}

${isKorean ? '분석 지침:' : 'Analysis Guidelines:'}
${isKorean 
  ? '- 선호 분위기와 활동을 기반으로 적절한 장르 추론\n- 구체적이고 검색 가능한 키워드 생성\n- 사용자 선호사항에 맞는 논리적 근거 제공'
  : '- Infer appropriate genres based on preferred mood and activity\n- Generate specific and searchable keywords\n- Provide logical reasoning based on user preferences'}

${STRICT_FORMAT}
`;
    }

    const lines = playlistHistory.map(p =>
      `- ${p.title} (${p.playCount || 1}회, ${new Date(p.timestamp).toLocaleDateString()})`
    ).join('\n');

    const totalPlays = playlistHistory.reduce((sum, p) => sum + (p.playCount || 1), 0);
    const mostPlayed = playlistHistory.reduce((max, p) =>
      (p.playCount || 1) > (max.playCount || 1) ? p : max
    );

    // 재생 패턴 분석
    const playPatterns = playlistHistory.map(p => ({
      title: p.title,
      playCount: p.playCount || 1,
      weight: p.playCount || 1
    })).sort((a, b) => b.weight - a.weight);

    return `
${isKorean ? '다음 플레이리스트 히스토리를 정확히 분석하세요:' : 'Accurately analyze the playlist history below:'}

${isKorean ? '재생 기록:' : 'Play History:'}
${lines}

${isKorean ? '재생 패턴 분석:' : 'Play Pattern Analysis:'}
${isKorean ? '- 총 재생 횟수:' : '- Total Plays:'} ${totalPlays}
${isKorean ? '- 최다 재생 플레이리스트:' : '- Most Played Playlist:'} ${mostPlayed.title} (${mostPlayed.playCount || 1}회)
${isKorean ? '- 재생 빈도 순위:' : '- Play Frequency Ranking:'}
${playPatterns.slice(0, 3).map((p, i) => `${i + 1}. ${p.title} (${p.playCount}회)`).join('\n')}

${isKorean ? '사용자 선호사항:' : 'User Preferences:'} ${JSON.stringify(userPreferences)}

${isKorean ? '분석 지침:' : 'Analysis Guidelines:'}
${isKorean 
  ? '1. 플레이리스트 제목에서 구체적인 장르와 분위기를 정확히 추출하세요\n2. 재생 횟수가 높은 플레이리스트에 더 높은 가중치를 부여하세요\n3. 제목에 포함된 키워드를 기반으로 정확한 장르 분류를 하세요:\n   - 팝/재즈/락/클래식/일렉트로닉/힙합/컨트리/라틴/레게 등\n   - 차분한/운동/공부/파티/수면/로맨틱/추억 등\n4. 사용자 선호사항과 실제 재생 기록을 종합하여 일관성 있는 추천을 생성하세요\n5. YouTube에서 실제 검색 가능한 구체적인 키워드를 제시하세요\n6. 새로운 장르나 분위기가 나타나면 기존 분석을 업데이트하세요'
  : '1. Extract specific genres and moods accurately from playlist titles\n2. Give higher weight to playlists with higher play counts\n3. Classify genres accurately based on keywords in titles:\n   - pop/jazz/rock/classical/electronic/hiphop/country/latin/reggae etc.\n   - chill/workout/study/party/sleep/romantic/nostalgic etc.\n4. Generate consistent recommendations by combining user preferences with actual play history\n5. Provide specific keywords that can actually be searched on YouTube\n6. Update analysis when new genres or moods appear'}

${STRICT_FORMAT}
${isKorean
  ? '주의: recommendedSongs, songs, tracks 등의 키는 절대 사용하지 마세요. 플레이리스트 제목과 재생 횟수를 정확히 분석하여 구체적이고 검색 가능한 결과를 제공하세요. 새로운 장르가 나타나면 기존 분석을 업데이트하세요.'
  : 'Note: DO NOT use keys like recommendedSongs, songs, or tracks. Accurately analyze playlist titles and play counts to provide specific and searchable results. Update analysis when new genres appear.'}
`;
  }

  // 플레이리스트 내용을 포함한 고급 분석 프롬프트
  buildMusicAnalysisPromptWithContent(playlistHistory, userPreferences, language = 'ko') {
    const isKorean = language === 'ko';

    // 공통 형식 지시
    const STRICT_FORMAT = isKorean
      ? `출력 형식: JSON 하나의 오브젝트로만 응답.
반드시 다음 "키만" 포함하고 그 외 키는 절대 포함하지 마세요:
- preferredGenres: string[]   // 사용자가 선호하는 장르 (구체적이고 정확한 장르명)
- preferredMood: string[]     // 사용자의 선호 분위기/무드 (감정적 특성)
- recommendedKeywords: string[] // YouTube 검색용 키워드 (실제 검색 가능한 구체적 키워드)
- reasoning: string           // 분석 근거 (플레이리스트 제목과 실제 곡 정보를 기반으로 한 구체적 분석)`
      : `Output format: Respond with a single JSON object ONLY.
Include EXACTLY these keys and NO others:
- preferredGenres: string[] (specific and accurate genre names)
- preferredMood: string[] (emotional characteristics)
- recommendedKeywords: string[] (concrete searchable keywords for YouTube)
- reasoning: string (analysis based on playlist titles and actual song information)`;

    if (!playlistHistory || playlistHistory.length === 0) {
      return `
${isKorean
  ? '사용자에게 플레이리스트 히스토리가 없습니다. 사용자 선호사항을 기반으로 일반적인 음악 취향을 분석하세요.'
  : 'The user has no playlist history. Analyze general music preferences based on user preferences.'}

${isKorean ? '사용자 선호사항:' : 'User Preferences:'} ${JSON.stringify(userPreferences)}

${isKorean ? '분석 지침:' : 'Analysis Guidelines:'}
${isKorean 
  ? '- 플레이리스트 제목에서 구체적인 장르와 분위기를 정확히 추출하세요\n- 실제 곡 정보를 통해 더 정확한 장르 추론\n- 사용자 선호사항과 실제 재생 기록을 종합하여 일관성 있는 추천을 생성하세요\n- YouTube에서 실제 검색 가능한 구체적인 키워드를 제시하세요\n- 새로운 장르나 분위기가 나타나면 기존 분석을 업데이트하세요.'
  : '- Extract specific genres and moods accurately from playlist titles\n- Infer more accurate genres through actual song information\n- Generate consistent recommendations by combining user preferences with actual play history\n- Provide specific keywords that can actually be searched on YouTube\n- Update analysis when new genres or moods appear.'}

${STRICT_FORMAT}
${isKorean
  ? '주의: recommendedSongs, songs, tracks 등의 키는 절대 사용하지 마세요. 플레이리스트 제목과 실제 곡 정보를 정확히 분석하여 구체적이고 검색 가능한 결과를 제공하세요. 새로운 장르가 나타나면 기존 분석을 업데이트하세요.'
  : 'Note: DO NOT use keys like recommendedSongs, songs, or tracks. Accurately analyze playlist titles and actual song information to provide specific and searchable results. Update analysis when new genres appear.'}
`;
    }

    // 플레이리스트 정보와 곡 정보를 함께 구성
    const playlistDetails = playlistHistory.map(p => {
      let detail = `- ${p.title} (${p.playCount || 1}회, ${new Date(p.timestamp).toLocaleDateString()})`;
      
      // 실제 곡 정보가 있는 경우 추가
      if (p.songs && p.songs.length > 0) {
        const songTitles = p.songs.map(s => s.title).slice(0, 5); // 최대 5개 곡만
        detail += `\n  곡들: ${songTitles.join(', ')}`;
      }
      
      return detail;
    }).join('\n');

    const totalPlays = playlistHistory.reduce((sum, p) => sum + (p.playCount || 1), 0);
    const mostPlayed = playlistHistory.reduce((max, p) =>
      (p.playCount || 1) > (max.playCount || 1) ? p : max
    );

    // 재생 패턴 분석
    const playPatterns = playlistHistory.map(p => ({
      title: p.title,
      playCount: p.playCount || 1,
      weight: p.playCount || 1
    })).sort((a, b) => b.weight - a.weight);

    return `
${isKorean ? '다음 플레이리스트 히스토리와 실제 곡 정보를 정확히 분석하세요:' : 'Accurately analyze the playlist history and actual song information below:'}

${isKorean ? '재생 기록 (곡 정보 포함):' : 'Play History (with song information):'}
${playlistDetails}

${isKorean ? '재생 패턴 분석:' : 'Play Pattern Analysis:'}
${isKorean ? '- 총 재생 횟수:' : '- Total Plays:'} ${totalPlays}
${isKorean ? '- 최다 재생 플레이리스트:' : '- Most Played Playlist:'} ${mostPlayed.title} (${mostPlayed.playCount || 1}회)
${isKorean ? '- 재생 빈도 순위:' : '- Play Frequency Ranking:'}
${playPatterns.slice(0, 3).map((p, i) => `${i + 1}. ${p.title} (${p.playCount}회)`).join('\n')}

${isKorean ? '사용자 선호사항:' : 'User Preferences:'} ${JSON.stringify(userPreferences)}

${isKorean ? '분석 지침:' : 'Analysis Guidelines:'}
${isKorean 
  ? '1. 플레이리스트 제목과 실제 곡 정보를 모두 고려하여 정확한 장르를 분석하세요\n2. "도미도미도레미" 같은 제목은 음악 이론/교육용 콘텐츠일 가능성이 높으니 곡 정보를 통해 실제 장르를 확인하세요\n3. 재생 횟수가 높은 플레이리스트에 더 높은 가중치를 부여하세요\n4. 곡 제목에서 장르, 아티스트, 분위기 키워드를 추출하여 정확한 분류를 하세요:\n   - 팝/재즈/락/클래식/일렉트로닉/힙합/컨트리/라틴/레게/펑크/메탈/소울/포크/인디 등\n   - 차분한/운동/공부/파티/수면/로맨틱/추억/스트레스/집중/휴식/드라이브 등\n5. 사용자 선호사항과 실제 재생 기록을 종합하여 일관성 있는 추천을 생성하세요\n6. YouTube에서 실제 검색 가능한 구체적인 키워드를 제시하세요\n7. 새로운 장르나 분위기가 나타나면 기존 분석을 업데이트하세요'
  : '1. Analyze accurate genres by considering both playlist titles and actual song information\n2. For titles like "Do Re Mi", consider they might be music theory/educational content and verify actual genre through song information\n3. Give higher weight to playlists with higher play counts\n4. Extract genre, artist, mood keywords from song titles for accurate classification:\n   - pop/jazz/rock/classical/electronic/hiphop/country/latin/reggae/punk/metal/soul/folk/indie etc.\n   - chill/workout/study/party/sleep/romantic/nostalgic/stress/focus/relax/drive etc.\n5. Generate consistent recommendations by combining user preferences with actual play history\n6. Provide specific keywords that can actually be searched on YouTube\n7. Update analysis when new genres or moods appear'}

${STRICT_FORMAT}
${isKorean
  ? '주의: recommendedSongs, songs, tracks 등의 키는 절대 사용하지 마세요. 플레이리스트 제목과 실제 곡 정보를 정확히 분석하여 구체적이고 검색 가능한 결과를 제공하세요. "도미도미도레미" 같은 제목은 곡 정보를 통해 실제 장르를 파악하세요.'
  : 'Note: DO NOT use keys like recommendedSongs, songs, or tracks. Accurately analyze playlist titles and actual song information to provide specific and searchable results. For titles like "Do Re Mi", determine actual genre through song information.'}
`;
  }

  // ---------- 기타 유틸 요청 ----------
  async getRealTimeRecommendations(currentSong, mood) {
    if (!this.isConfigured()) {
      return { success: false, error: 'OpenAI API 키가 설정되지 않았습니다.' };
    }

    try {
      const prompt = `
현재 곡: ${currentSong}
현재 분위기: ${mood}

YouTube에서 바로 검색할 수 있는 추천 검색어를 JSON으로만 반환하세요.
키:
- keywords: string[]  // 3~5개
`;

      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: '너는 음악 추천 전문가다. 반드시 JSON만 출력하라.' },
            { role: 'user', content: prompt }
          ],
          max_tokens: 200,
          temperature: 0.4
        })
      });

      if (!response.ok) throw new Error(`API 요청 실패: ${response.status}`);

      const data = await response.json();
      const content = data?.choices?.[0]?.message?.content ?? '';
      const parsed = this.extractFirstJSONObject(content);
      const keywords = Array.isArray(parsed?.keywords) ? parsed.keywords.map(String).filter(Boolean) : [];

      return { success: true, recommendations: keywords };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async makeRequest(prompt) {
    if (!this.isConfigured()) {
      return { success: false, error: 'OpenAI API 키가 설정되지 않았습니다.' };
    }
    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.apiKey}` },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: '반드시 JSON만 출력하라.' },
            { role: 'user', content: prompt }
          ],
          max_tokens: 500,
          temperature: 0.3
        })
      });

      if (!response.ok) throw new Error(`API 요청 실패: ${response.status}`);
      const data = await response.json();
      return { success: true, content: data.choices?.[0]?.message?.content ?? '' };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

const aiService = new AIService();
export default aiService;
